import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductLibrarayService } from '../../services/product-libraray.service';
import { CreateProductDTO, DigitalSpecsDTO, FlexoSpecsDTO, OffsetSpecsDTO, ProductionDataDTO } from '../../interface/product-library.entity';
import { NotificationService, DotLoadingServices } from '../../../../core';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-product-library-create',
  imports: [ CommonModule, FormsModule],
  templateUrl: './product-library-create.html',
  styleUrl: './product-library-create.scss'
})
export class ProductLibraryCreate {

  private productLibraryService = inject(ProductLibrarayService);
  private notification = inject(NotificationService);
  private loading = inject(DotLoadingServices);
  protected router = inject(Router);
  protected route = inject(ActivatedRoute);

  isEdit = false;
  
  selectedDepartment: string = '';
  category: 'Cartoon' | 'Gift Box' | 'Book' | 'Label' | '' = '';
  isCategoryEnabled: boolean = false;

  productName: string = '';
  customer: string = '';
  productCode: string = '';
  customerProductCode: string = '';
  salesOrder: string = '';
  quotationNo: string = '';
  artworkNo: string = '';
  version: string = '';
  noOfColors: number | null = null;
  noOfUps: number | null = null;
  proofStandard: string = ''
  printingMachine: string = '';
  windingDirection: string = '';
  windingDirections: string[] = ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8'];
  coreSize: string = '';
  flexoType: string = ''; // 'Reel' | 'Sheet'
  pcsPerRoll: number | null = null;
  flexoRemark: string = '';
  widthMm: string = '';
  lengthMm: string = '';
  tolerance: number | null = null;
  cutSheetSize: number | null = null;
  fullSheetSize: number | null = null;
  productSize: number | null = null;
  selectedPastingType: string = '';
  selectedGiftBoxBoardType: string = '';
  thickness: number | null = null;
  selectedGiftBoxType: string = '';
  selectedBookCoverType: string = '';
  coverThickness: number | null = null;
  coverLength: number | null = null;
  coverWidth: number | null = null;
  innerpageLength: number | null = null;
  innerpageWidth: number | null = null;
  selectedPageBindingType: string = '';
  innerPageLength: number | null = null;



  // Customer search dropdown
  allCustomers = signal<{ id: number; customerName: string }[]>([]);
  filteredCustomers = signal<{ id: number; customerName: string }[]>([]);
  customerDropdownOpen = signal<boolean>(false);
  customerId = signal<number | null>(null);

  machines = signal<any[]>([]);
  finishingsList = signal<any[]>([]);

  machineSearch: string[] = [];
  finishingSearch: string[] = [];

  machineDropdownOpen: number | null = null;
  finishingDropdownOpen: number | null = null;
  printingMachineDropdownOpen: boolean = false;

  finishings = signal<{ stepId: string; sequence: number; process: string; machine: string; remarks: string }[]>([
    { stepId: 'f1', sequence: 1, process: '', machine: '', remarks: '' }
  ]);

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      const mode = params['mode'];

      if (code && mode === 'edit') {
        this.isEdit = true;
        this.loadProductForEdit(code);
      }
    });

    this.productLibraryService.getMachines().subscribe({
      next: (res) => this.machines.set(res.data?.machines ?? []),
      error: (err) => console.error('Failed to load machines', err)
    });

    this.productLibraryService.getFinishings().subscribe({
      next: (res) => this.finishingsList.set(res.data ?? []),
      error: (err) => console.error('Failed to load finishings', err)
    });

    this.productLibraryService.getAllCustomers().subscribe({
      next: (data) => this.allCustomers.set(data.filter(c => !!c.customerName)),
      error: (err) => console.error('Failed to load customers', err)
    });

    this.finishingSearch[0] = '';
    this.machineSearch[0] = '';
  }

  // ── Customer search helpers ────────────────────────────────────────────────
  filterCustomers(query: string) {
    const val = (query || '').toLowerCase();
    const matches = !val
      ? this.allCustomers()
      : this.allCustomers().filter(c => c.customerName.toLowerCase().includes(val));
    this.filteredCustomers.set(matches);
    this.customerDropdownOpen.set(matches.length > 0);
  }

  selectCustomer(c: { id: number; customerName: string }) {
    this.customer = c.customerName;
    this.customerId.set(c.id);
    this.customerDropdownOpen.set(false);
    this.validationErrors.customer = false;
  }

  hideCustomerDropdown() {
    setTimeout(() => this.customerDropdownOpen.set(false), 200);
  }

  onCustomerChange(val: string) {
    this.filterCustomers(val);
  }

  onCustomerFocus() {
    this.filterCustomers(this.customer);
  }

  filteredMachines(index: number, capability?: string) {
    const search = (this.machineSearch[index] || '').toLowerCase();
    return this.machines().filter(m =>
        (!capability || m.capabilities?.toLowerCase().includes(capability.toLowerCase())) &&
        m.machineName.toLowerCase().includes(search)
    );
  }

  filteredFinishings(index: number) {
    const search = (this.finishingSearch[index] || '').toLowerCase();
    return this.finishingsList().filter(f =>
        f.Finishing.toLowerCase().includes(search)
    );
  }

  selectMachine(index: number, machineName: string) {
    this.finishings.update(list =>
        list.map((item, i) => i === index ? { ...item, machine: machineName } : item)
    );
    this.machineSearch[index] = machineName;
    this.machineDropdownOpen = null;
  }

  selectFinishing(index: number, finishingName: string) {
    this.finishings.update(list =>
        list.map((item, i) => i === index ? { ...item, process: finishingName } : item)
    );
    this.finishingSearch[index] = finishingName;
    this.finishingDropdownOpen = null;
  }

  filteredPrintingMachines() {
    const search = (this.printingMachine || '').toLowerCase();
    return this.machines().filter(m =>
        m.capabilities?.toLowerCase().includes('printing') &&
        m.machineName.toLowerCase().includes(search)
    );
  }

  selectPrintingMachine(machineName: string) {
    this.printingMachine = machineName;
    this.printingMachineDropdownOpen = false;
  }

  onPrintingMachineBlur() {
    setTimeout(() => {
      this.printingMachineDropdownOpen = false;
    }, 200);
  }
  onFinishingBlur(index: number) {
    setTimeout(() => {
      this.finishingDropdownOpen = null;
      const current = this.finishings()[index];
      if (!this.finishingSearch[index]) {
        this.finishingSearch[index] = current.process;
      }
    }, 200);
  }

  onMachineBlur(index: number) {
    setTimeout(() => {
      this.machineDropdownOpen = null;
      const current = this.finishings()[index];
      if (!this.machineSearch[index]) {
        this.machineSearch[index] = current.machine;
      }
    }, 200);
  }

private loadProductForEdit(code: string) {
  this.loading.set(true);
  this.productLibraryService.getProductById(code).subscribe({
    next: (res) => {
      const product = res.data;

      // --- General Data ---
      this.productCode = product.productCode;
      this.productName = product.generalData?.productName ?? '';
      this.customer = product.generalData?.customer ?? '';
      this.customerProductCode = product.generalData?.customerProductCode ?? '';
      this.salesOrder = product.generalData?.references?.[0]?.salesOrder ?? '';
      this.quotationNo = product.generalData?.references?.[0]?.quotationNo ?? '';
      this.artworkNo = product.generalData?.artwork?.[0]?.artworkNo ?? '';
      this.version = product.generalData?.artwork?.[0]?.version ?? '';

      // --- Department & Category ---
      this.selectedDepartment = product.department ?? '';
      this.onDepartmentChange();

      this.category = product.generalData?.category ?? '';
      this.onCategoryChange();

      // --- Finishings ---
      const mappedFinishings = product.finishings?.map(f => ({
        stepId: f.stepId ?? '',
        sequence: f.sequence ?? 0,
        process: f.process ?? '',
        machine: f.machine ?? '',
        remarks: f.remarks ?? ''
      })) ?? [];

      this.finishings.set(mappedFinishings);

      // Pre-populate the search arrays so inputs show existing values immediately
      mappedFinishings.forEach((f, i) => {
        this.finishingSearch[i] = f.process;
        this.machineSearch[i] = f.machine;
      });

      // --- Technical Data ---
      const td = product.technicalData as OffsetSpecsDTO | FlexoSpecsDTO | DigitalSpecsDTO | undefined;

      if (td) {
        // Digital data — only print
        if (!('fullSheetSize' in td) && !('flexoType' in td) && 'print' in td) {
          const printData = (td as DigitalSpecsDTO).print?.[0];
          if (printData) {
            this.noOfColors = printData.noOfColors ?? 0;
            this.noOfUps = printData.noOfUps ?? 0;
            this.proofStandard = printData.proofStandard ?? '';
            this.printingMachine = printData.printingMachine ?? '';
          }
        }

        // Offset data
        if ('fullSheetSize' in td) {
          this.fullSheetSize = td.fullSheetSize ?? 0;
          this.cutSheetSize = td.cutSheetSize ?? 0;
          this.productSize = td.productSize ?? 0;
          this.selectedPastingType = (td as OffsetSpecsDTO).pastingType ?? '';
          this.widthMm = (td as OffsetSpecsDTO).widthMm != null ? String((td as OffsetSpecsDTO).widthMm) : '';
          this.lengthMm = (td as OffsetSpecsDTO).lengthMm != null ? String((td as OffsetSpecsDTO).lengthMm) : '';

          // Gift Box
          if ((td as OffsetSpecsDTO).giftBox) {
            this.selectedGiftBoxBoardType = (td as OffsetSpecsDTO).giftBox?.boardType ?? '';
            this.thickness = (td as OffsetSpecsDTO).giftBox?.thickness ?? 0;
            this.selectedGiftBoxType = (td as OffsetSpecsDTO).giftBox?.boxType ?? '';
          }

          // Book
          if ((td as OffsetSpecsDTO).book) {
            const book = (td as OffsetSpecsDTO).book!;
            this.selectedBookCoverType = book.coverType ?? '';
            this.coverThickness = book.coverThickness ?? 0;
            this.coverWidth = book.coverWidth ?? 0;
            this.coverLength = book.coverLength ?? 0;
            this.innerpageWidth = book.innerPageWidth ?? 0;
            this.innerpageLength = book.innerPageLength ?? 0;
            this.selectedPageBindingType = book.bindingType ?? '';
          }

          // Print
          const printData = (td as OffsetSpecsDTO).print?.[0];
          if (printData) {
            this.noOfColors = printData.noOfColors ?? 0;
            this.noOfUps = printData.noOfUps ?? 0;
            this.proofStandard = printData.proofStandard ?? '';
            this.printingMachine = printData.printingMachine ?? '';
          }
        }

        // Flexo data
        if ('flexoType' in td) {
          this.flexoType = (td as FlexoSpecsDTO).flexoType ?? '';
          this.coreSize = (td as FlexoSpecsDTO).coreSize ?? '';
          this.noOfUps = (td as FlexoSpecsDTO).noOfUps ?? null;
          this.pcsPerRoll = (td as FlexoSpecsDTO).pcsPerRoll ?? null;
          this.windingDirection = (td as FlexoSpecsDTO).windingDirection ?? '';
          this.flexoRemark = (td as FlexoSpecsDTO).remark ?? '';
          this.widthMm = (td as FlexoSpecsDTO).widthMm != null ? String((td as FlexoSpecsDTO).widthMm) : '';
          this.lengthMm = (td as FlexoSpecsDTO).lengthMm != null ? String((td as FlexoSpecsDTO).lengthMm) : '';
          this.tolerance = (td as FlexoSpecsDTO).tolerance ?? null;

          const printData = (td as FlexoSpecsDTO).print?.[0];
          if (printData) {
            this.noOfColors = printData.noOfColors ?? 0;
            this.noOfUps = printData.noOfUps ?? 0;
            this.proofStandard = printData.proofStandard ?? '';
            this.printingMachine = printData.printingMachine ?? '';
          }
        }
      }
      this.loading.set(false);
    },
    error: (err) => {
      console.error('Failed to load product for edit', err);
      this.loading.set(false);
    }
  });
}



  goBack() {
    this.router.navigate(['/product-library']);
    this.resetForm();
  }

  onDepartmentChange() {
    console.log('Selected department:', this.selectedDepartment);

    if (this.selectedDepartment === 'Digital' || this.selectedDepartment === 'Offset') {
      this.isCategoryEnabled = true;
    } else {
      this.isCategoryEnabled = false;
      this.category = '';
    }
  }

  onCategoryChange() {
    console.log('Selected category:', this.category);
  }

  onFlexoTypeChange() {
    console.log('Selected Flexo type:', this.flexoType);
    // Sheet has no reel-only fields — clear them, but keep the remark
    if (this.flexoType !== 'Reel') {
      this.coreSize = '';
      this.noOfUps = null;
      this.pcsPerRoll = null;
      this.windingDirection = '';
    }
  }

  onPastingTypeChange() {
    console.log('Selected Pasting Type:', this.selectedPastingType);
  }

  onGiftBoxBoardTypeChange() {
    console.log('Selected box board Type:', this.selectedGiftBoxBoardType);
  }

  onGiftBoxTypeChange() {
    console.log('Selected gift box Type:', this.selectedGiftBoxType);
  }

  onBookCoverTypeChange() {
    console.log('Selected book cover Type:', this.selectedBookCoverType);
  }

  onPageBindingTypeChange() {
    console.log('Selected book binding Type:', this.selectedPageBindingType);
  }

  addFinishing() {
    const current = this.finishings();
    const nextIndex = current.length + 1;

    const newItem = {
      stepId: `f${nextIndex}`,
      sequence: nextIndex,
      process: '',
      machine: '',
      remarks: ''
    };
    this.finishingSearch[nextIndex - 1] = '';
    this.machineSearch[nextIndex - 1] = '';

    this.finishings.update(list => [...list, newItem]);
  }

  removeFinishing(index: number) {
    this.finishings.update(list => {
      if (list.length <= 1) return list;

      const updated = list.filter((_, i) => i !== index);

      return updated.map((item, i) => ({
        ...item,
        stepId: `f${i + 1}`,
        sequence: i + 1
      }));
    });

  }

  validationErrors = {
    department: false,
    productName: false,
    customer: false,
    salesOrder: false,
    quotationNo: false,
    flexoType: false
  };

  private validateGeneralDetails(): boolean {
    this.validationErrors = {
      department: !this.selectedDepartment,
      productName: !this.productName?.trim(),
      customer: !this.customer?.trim(),
      salesOrder: !this.salesOrder?.trim(),
      quotationNo: !this.quotationNo?.trim(),
      flexoType: this.selectedDepartment === 'Flexo' && !this.flexoType
    };

    return !Object.values(this.validationErrors).some(Boolean);
  }

  saveProduct() {
    if (!this.validateGeneralDetails()) {
      this.notification.set({
        type: 'error',
        message: 'Please fill all required General Details.'
      });
      return;
    }
    
    const payload: CreateProductDTO = {
      department: this.selectedDepartment as 'Flexo' | 'Offset' | 'Digital',
      generalData: {
        customerProductCode: this.customerProductCode,
        productName: this.productName,
        customer: this.customer,
        category: this.category || undefined,
        references: [
          {
            salesOrder: this.salesOrder,
            quotationNo: this.quotationNo
          }
        ],
        artwork: [
          {
            artworkNo: this.artworkNo,
            version: this.version
          }
        ]
      },
      technicalData: this.buildSpecifications() || undefined,
      finishings: this.finishings(),
      productionData: {
        materials: [],
        inks: [],
        varnishes: [],
        screens: [],
        coldFoil: '',
        hotFoil: '',
        embossing: '',
        lamination: '',
        dieCutCode: '',
        productionDataAdded: false
      }
    };

    this.loading.set(true);
    if (this.isEdit) {
      // Use update method
      this.productLibraryService.updateProduct(this.productCode, payload).subscribe({
        next: res => {
          console.log('Product updated successfully', res);
          this.notification.set({ type: 'success', message: 'Product updated successfully.' });
          this.resetForm();
          this.loading.set(false);
        },
        error: err => {
          console.error('Failed to update product', err);
          this.notification.set({ type: 'error', message: 'Failed to update product.' });
          this.loading.set(false);
        }
      });
    } else {
      // Use create method
      this.productLibraryService.createProduct(payload).subscribe({
        next: res => {
          console.log('Product created successfully', res);
          this.notification.set({ type: 'success', message: 'Product created successfully.' });
          this.resetForm();
          this.loading.set(false);
        },
        error: err => {
          console.error('Failed to create product', err);
          this.notification.set({
            type: 'error',
            message: err?.error?.message || 'Failed to create product.'
          });
          this.loading.set(false);
        }
      });
    }
  }



private buildSpecifications(): OffsetSpecsDTO | FlexoSpecsDTO | DigitalSpecsDTO {
  if (this.selectedDepartment === 'Digital') {
    return {
      print: [
        {
          noOfColors: this.noOfColors!,
          noOfUps: this.noOfUps!,
          proofStandard: this.proofStandard!,
          printingMachine: this.printingMachine!
        }
      ]
    };
  }

  if (this.selectedDepartment === 'Offset') {
    return {
      fullSheetSize: this.fullSheetSize!,
      cutSheetSize: this.cutSheetSize!,
      productSize: this.productSize!,
      pastingType: this.selectedPastingType || undefined,
      widthMm: this.widthMm,
      lengthMm: this.lengthMm,
      giftBox: this.category === 'Gift Box' ? {
        boardType: this.selectedGiftBoxBoardType!,
        thickness: this.thickness!,
        boxType: this.selectedGiftBoxType!
      } : undefined,
      book: this.category === 'Book' ? {
        coverType: this.selectedBookCoverType!,
        coverThickness: this.coverThickness!,
        coverWidth: this.coverWidth!,
        coverLength: this.coverLength!,
        innerPageWidth: this.innerpageWidth!,
        innerPageLength: this.innerPageLength!,
        bindingType: this.selectedPageBindingType!
      } : undefined,
      print: [
        {
          noOfColors: this.noOfColors!,
          noOfUps: this.noOfUps!,
          proofStandard: this.proofStandard!,
          printingMachine: this.printingMachine!
        }
      ]
    };
  }

  if (this.selectedDepartment === 'Flexo') {
    const isReel = this.flexoType === 'Reel';
    return {
      flexoType: this.flexoType,
      coreSize: isReel ? this.coreSize : undefined,
      noOfUps: isReel ? this.noOfUps : undefined,
      pcsPerRoll: isReel ? this.pcsPerRoll : undefined,
      windingDirection: isReel ? this.windingDirection : undefined,
      tolerance: isReel ? this.tolerance : undefined,
      remark: this.flexoRemark || undefined,
      widthMm: this.widthMm,
      lengthMm: this.lengthMm,
      print: [
        {
          noOfColors: this.noOfColors!,
          noOfUps: this.noOfUps!,
          proofStandard: this.proofStandard!,
          printingMachine: this.printingMachine!
        }
      ]
    };
  }

  return undefined as any;
}

private resetForm() {
  this.selectedDepartment = '';
  this.category = '';
  this.isCategoryEnabled = false;

  this.productName = '';
  this.customer = '';
  this.customerId.set(null);
  this.customerDropdownOpen.set(false);
  this.filteredCustomers.set([]);
  this.productCode = '';
  this.customerProductCode = '';
  this.salesOrder = '';
  this.quotationNo = '';
  this.artworkNo = '';
  this.version = '';

  this.noOfColors = null;
  this.noOfUps = null;
  this.proofStandard = '';
  this.printingMachine = '';

  this.flexoType = '';
  this.coreSize = '';
  this.pcsPerRoll = null;
  this.windingDirection = '';
  this.flexoRemark = '';
  this.widthMm = '';
  this.lengthMm = '';
  this.tolerance = null;

  this.fullSheetSize = null;
  this.cutSheetSize = null;
  this.productSize = null;
  this.selectedPastingType = '';

  this.selectedGiftBoxBoardType = '';
  this.thickness = null;
  this.selectedGiftBoxType = '';

  this.selectedBookCoverType = '';
  this.coverThickness = null;
  this.coverWidth = null;
  this.coverLength = null;
  this.innerpageWidth = null;
  this.innerpageLength = null;
  this.innerPageLength = null;
  this.selectedPageBindingType = '';

  this.finishings.set([
    { stepId: 'f1', sequence: 1, process: '', machine: '', remarks: '' }
  ]);
}



}
